import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth, api } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Camera, Clock, CheckCircle, XCircle, Upload } from 'lucide-react';
import { toast } from 'sonner';

const MyUploads = () => {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [uploads, setUploads] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!currentUser) {
      navigate('/login');
      return;
    }
    fetchUploads();
  }, [currentUser, navigate]);

  const fetchUploads = async () => {
    try {
      setLoading(true);
      const response = await api.get('/api/gallery/my-submissions');
      setUploads(response.data);
    } catch (error) {
      console.error('Error fetching uploads:', error);
      toast.error('Failed to load your uploads');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    switch (status) {
      case 'approved':
        return (
          <Badge className="bg-lime-500 text-black">
            <CheckCircle className="w-3 h-3 mr-1" />
            Approved
          </Badge>
        );
      case 'pending':
        return (
          <Badge className="bg-yellow-500 text-black">
            <Clock className="w-3 h-3 mr-1" />
            Pending Review
          </Badge>
        );
      case 'rejected':
        return (
          <Badge className="bg-red-500 text-white">
            <XCircle className="w-3 h-3 mr-1" />
            Not Approved
          </Badge>
        );
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-black text-white flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-lime-400 border-t-transparent mx-auto mb-4"></div>
          <p className="text-zinc-400">Loading your uploads...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black text-white py-16">
      <div className="container mx-auto px-4 max-w-6xl">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold mb-2">
              <Camera className="inline-block w-10 h-10 mr-3 text-lime-400" />
              My Photo Uploads
            </h1>
            <p className="text-zinc-400">Track your submitted photos and their status</p>
          </div>
          <Button
            onClick={() => navigate('/share-style')}
            className="mt-4 md:mt-0 bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
          >
            <Upload className="w-4 h-4 mr-2" />
            Upload New Photo
          </Button>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Total Uploads</p>
                  <p className="text-3xl font-bold text-white">{uploads.length}</p>
                </div>
                <Camera className="w-8 h-8 text-purple-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Approved</p>
                  <p className="text-3xl font-bold text-lime-400">
                    {uploads.filter(u => u.status === 'approved').length}
                  </p>
                </div>
                <CheckCircle className="w-8 h-8 text-lime-400" />
              </div>
            </CardContent>
          </Card>

          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-zinc-400 text-sm">Pending</p>
                  <p className="text-3xl font-bold text-yellow-400">
                    {uploads.filter(u => u.status === 'pending').length}
                  </p>
                </div>
                <Clock className="w-8 h-8 text-yellow-400" />
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Uploads Grid */}
        {uploads.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {uploads.map(upload => (
              <Card key={upload.id} className="bg-zinc-900 border-zinc-800 overflow-hidden">
                <div className="relative">
                  <img
                    src={upload.url}
                    alt={upload.alt}
                    className="w-full h-64 object-cover"
                  />
                  <div className="absolute top-3 right-3">
                    {getStatusBadge(upload.status)}
                  </div>
                </div>
                <CardContent className="p-4">
                  <div className="space-y-2">
                    <p className="text-zinc-400 text-sm">
                      Uploaded {new Date(upload.created_at).toLocaleDateString()}
                    </p>
                    
                    {upload.status === 'pending' && (
                      <p className="text-yellow-400 text-sm">
                        ⏳ Awaiting admin review
                      </p>
                    )}
                    
                    {upload.status === 'approved' && (
                      <p className="text-lime-400 text-sm">
                        ✅ Live in gallery! View on the Gallery page
                      </p>
                    )}
                    
                    {upload.status === 'rejected' && (
                      <p className="text-zinc-500 text-sm">
                        This photo didn't meet our guidelines. Try uploading a different photo!
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        ) : (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-12 text-center">
              <Camera className="w-16 h-16 mx-auto mb-4 text-zinc-600" />
              <h3 className="text-xl font-semibold mb-2">No Uploads Yet</h3>
              <p className="text-zinc-400 mb-6">
                Share your fresh cut with the Sauce Sity community!
              </p>
              <Button
                onClick={() => navigate('/share-style')}
                className="bg-gradient-to-r from-lime-500 to-lime-600 text-black hover:from-lime-600 hover:to-lime-700"
              >
                <Upload className="w-4 h-4 mr-2" />
                Upload Your First Photo
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default MyUploads;